/**
 * Global mixin: refresh data when a keep-alive cached component re-activates.
 *
 * App.vue wraps <router-view> in <keep-alive :max="11">. Cached instances are
 * returned without re-running mounted(), which leaves list/form views with
 * stale data after navigating away and back.
 *
 * Resolution order on activated() (after the initial mount-activation pair):
 *   1. component.refreshOnActivate()   — explicit hook (forms / custom views)
 *   2. component.loadData()            — common list convention
 *   3. component.loadReport()          — report convention
 *   4. component.loadStatement()       — statement convention
 *   5. component.fetchData()           — alternative list convention (POs, SOs,
 *                                        vouchers, store/grn, store/cs,
 *                                        service-invoices, etc.)
 *
 * Components without any of these methods are unaffected. Non-routed components
 * are also unaffected because activated() only fires inside <keep-alive>.
 *
 * NOTE: This is the single source of truth for keep-alive refresh. The
 * filterPersistence mixin must not register a duplicate activated() hook —
 * that would double-fire loadData on every cache re-activation.
 */
export default {
  mounted() {
    // Set a per-instance flag the moment the component finishes its initial
    // mount. The very next activated() call (which Vue fires immediately after
    // the mount when inside <keep-alive>) will see this flag and skip — that
    // initial activation is paired with mounted(), which already loads data.
    this._keepAliveJustMounted = true;
  },
  activated() {
    if (this._keepAliveJustMounted) {
      this._keepAliveJustMounted = false;
      return;
    }
    if (typeof this.refreshOnActivate === "function") {
      this.refreshOnActivate();
    } else if (typeof this.loadData === "function") {
      this.loadData();
    } else if (typeof this.loadReport === "function") {
      this.loadReport();
    } else if (typeof this.loadStatement === "function") {
      this.loadStatement();
    } else if (typeof this.fetchData === "function") {
      this.fetchData();
    }
  },
};
